/**
 * 55. Stock by Location — matrix view; on-hand, available, reserved;
 * filters by warehouse/lot.
 *
 * Arabic and English (C7): every word from the catalogs; quantities are
 * numbers the locale writes. The warehouse filter offers the warehouses the
 * balances are in and filters them (it offered two invented warehouse names
 * and filtered nothing, which no translation could make true).
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  Badge,
  Toolbar,
  Select,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatQuantity } from "@/lib/money";

interface StockRow {
  id: string;
  productSku: string;
  productName: string;
  warehouseName: string;
  lotNumber: string | null;
  quantity: string;
  reservedQuantity: string;
  availableQuantity: string;
  valuation: string;
}

export function StockByLocationPage() {
  const { t } = useTranslation();
  const [warehouse, setWarehouse] = useState("");
  const { data, isLoading } = useApiList<StockRow>(
    ["inventory", "stock"],
    "/inventory/balances",
  );
  const rows = data?.items ?? [];
  const warehouses = [
    ...new Set(rows.map((r) => r.warehouseName).filter(Boolean)),
  ].sort();
  const shown = rows.filter((r) => !warehouse || r.warehouseName === warehouse);

  return (
    <div>
      <PageHeader
        title={t("inventory.stock.title")}
        subtitle={t("inventory.stock.subtitle")}
        actions={
          <PendingButton variant="primary" reason="notScheduled" slice="B1">
            {t("shared.export")}
          </PendingButton>
        }
      />
      <Toolbar>
        <Select
          aria-label={t("fields.warehouse")}
          value={warehouse}
          onChange={(e) => setWarehouse(e.target.value)}
        >
          <option value="">{t("inventory.stock.allWarehouses")}</option>
          {warehouses.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </Toolbar>
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card
          title={listTitle(
            t("inventory.stock.list"),
            warehouse ? shown.length : data.total,
          )}
        >
          <DataTable<StockRow>
            rows={shown}
            columns={[
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "productName", label: t("fields.product") },
              { key: "warehouseName", label: t("fields.warehouse") },
              {
                key: "lotNumber",
                label: t("fields.lot"),
                render: (r) =>
                  r.lotNumber ? (
                    <span className="atlas-mono">{r.lotNumber}</span>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "quantity",
                label: t("inventory.stock.onHand"),
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              {
                key: "reservedQuantity",
                label: t("inventory.stock.reserved"),
                numeric: true,
                render: (r) => formatQuantity(r.reservedQuantity),
              },
              {
                key: "availableQuantity",
                label: t("inventory.stock.available"),
                numeric: true,
                render: (r) => (
                  <span>
                    <Badge
                      tone={
                        Number(r.availableQuantity) > 0
                          ? "success"
                          : Number(r.availableQuantity) === 0
                            ? "default"
                            : "danger"
                      }
                    >
                      {formatQuantity(r.availableQuantity)}
                    </Badge>
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
